import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatINR, formatDate } from "../lib/api";
import { usePolling } from "../lib/usePolling";
import { generateLRPdf } from "../lib/pdf";
import { LRSplitModal } from "../components/LRSplitModal";
import { lrStatusClass } from "./LRs";
import { Parties } from "../components/lr-detail/Parties";
import { Transport } from "../components/lr-detail/Transport";
import { Cargo } from "../components/lr-detail/Cargo";
import { History } from "../components/lr-detail/History";
import { LiveTracking } from "../components/lr-detail/LiveTracking";
import { ArrowLeft, Printer, Scissors, Split, Share2, MessageCircle, Pencil } from "lucide-react";
import { RemindMeButton } from "../components/RemindMeButton";
import { PodThumb } from "../components/PodThumb";
import { Lifecycle } from "../components/lr-detail/Lifecycle";
import { LifecycleBadge } from "../components/LifecycleBadge";
import { toast } from "sonner";
import { useTabTitle } from "../lib/tabs";

function Stat({ label, value, testId }) {
  return (
    <div className="bg-white border border-[#E5E3DB] p-4">
      <div className="text-[11px] uppercase tracking-wide text-[#68665E]">{label}</div>
      <div className="text-lg font-semibold text-[#1A1A18] mt-1" data-testid={testId}>{value}</div>
    </div>
  );
}

export default function LRDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [company, setCompany] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [showSplit, setShowSplit] = useState(false);
  const [splitItems, setSplitItems] = useState([]);
  const [splitVehicles, setSplitVehicles] = useState([]);
  const [splitNumber, setSplitNumber] = useState("");
  const [splitFreight, setSplitFreight] = useState(0);
  const [splitting, setSplitting] = useState(false);

  const refresh = useCallback(() => {
    api.get(`/lrs/${id}/related`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [id]);
  useEffect(() => {
    refresh();
    api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => {});
  }, [id, refresh]);
  usePolling(refresh, 15000, !showSplit);

  useTabTitle(data?.lr?.lr_number ? (/^lr/i.test(data.lr.lr_number) ? data.lr.lr_number : `LR ${data.lr.lr_number}`) : null);

  if (!data) return <div className="text-sm text-[#68665E]">Loading…</div>;
  if (data.error) return <div className="text-sm text-[#8A2526]">LR not found.</div>;
  const lr = data.lr;
  const vehicles = (lr.vehicles && lr.vehicles.length) ? lr.vehicles : (lr.vehicle_number ? [{ vehicle_number: lr.vehicle_number, driver_name: lr.driver_name, driver_phone: lr.driver_phone }] : []);
  const pickups = (lr.pickups && lr.pickups.length) ? lr.pickups : [];
  const deliveries = (lr.deliveries && lr.deliveries.length) ? lr.deliveries : [];
  const carrier = lr.carrier || {};
  const hasCarrier = carrier.company || carrier.pickup_poc_name || carrier.delivery_poc_name || carrier.pickup_poc_phone || carrier.delivery_poc_phone || carrier.notes;
  const hirer = lr.hirer || {};
  const hasHirer = hirer.name || hirer.contact_name || hirer.contact_phone || hirer.gst || hirer.division;
  const docTypes = (() => {
    const base = ["EWB", "AWB", "INV", "DC", "POD", "Other"];
    const partyNames = [...pickups, ...deliveries].map((p) => p.company).concat([lr.consignor_name, lr.consignee_name, hirer.name]);
    const extra = customers.filter((c) => partyNames.includes(c.name)).flatMap((c) => c.doc_titles || []);
    return Array.from(new Set([...extra, ...base]));
  })();
  const lrItems = lr.items || [];

  function toggle(list, setFn, idx) {
    setFn(list.includes(idx) ? list.filter((x) => x !== idx) : [...list, idx]);
  }
  function openSplit() {
    setSplitItems([]); setSplitVehicles([]); setSplitNumber(""); setSplitFreight(0); setShowSplit(true);
  }
  async function shareTracking() {
    try {
      const { data } = await api.post(`/lrs/${lr.id}/share-link`);
      const url = `${window.location.origin}${data.path}`;
      await navigator.clipboard.writeText(url);
      toast.success("Public tracking link copied — share with anyone (no login needed)");
    } catch {
      toast.error("Could not create tracking link");
    }
  }
  async function shareWhatsApp() {
    try {
      const { data } = await api.post(`/lrs/${lr.id}/share-link`);
      const url = `${window.location.origin}${data.path}`;
      const msg = `Track your consignment *${lr.lr_number}*\n${lr.from_location || ""} -> ${lr.to_location || ""}\nStatus: ${String(lr.status || "").replace(/_/g, " ")}\n\n${url}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    } catch {
      toast.error("Could not create tracking link");
    }
  }
  async function doSplit() {
    if (splitItems.length === 0 && splitVehicles.length === 0) { toast.error("Select at least one item or vehicle to split off"); return; }
    setSplitting(true);
    try {
      const { data: newLr } = await api.post(`/lrs/${lr.id}/split`, {
        item_indexes: splitItems, vehicle_indexes: splitVehicles,
        lr_number: splitNumber.trim() || null, move_freight: Number(splitFreight) || 0,
      });
      toast.success(`Split off into ${newLr.lr_number}`);
      setShowSplit(false);
      navigate(`/lr/${newLr.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Split failed");
    } finally { setSplitting(false); }
  }

  return (
    <div data-testid="lr-detail-page">
      <button className="btn-ghost text-sm mb-4" onClick={() => navigate(-1)} data-testid="lr-detail-back"><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Lorry Receipt</div>
          <h1 className="font-headings text-4xl font-bold text-[#1A1A18] mt-1" data-testid="lr-detail-number">{lr.lr_number}</h1>
          <p className="text-sm text-[#68665E] mt-1 flex items-center gap-2 flex-wrap">{formatDate(lr.lr_date)} · <span className={lrStatusClass(lr.lifecycle?.effective_status || lr.status)}>{String(lr.lifecycle?.effective_status || lr.status).replace(/_/g, " ")}</span> {lr.lifecycle && <LifecycleBadge lifecycle={lr.lifecycle} />}</p>
          {(lr.split_from || (lr.split_into && lr.split_into.length > 0) || lr.from_quotation_number) && (
            <div className="flex flex-wrap items-center gap-2 mt-2 text-xs" data-testid="lr-detail-split-links">
              {lr.split_from && (
                <button className="inline-flex items-center gap-1 text-[#68665E] hover:text-[#C84B31] hover:underline" onClick={() => navigate(`/lr/${lr.split_from.id}`)}>
                  <Split className="w-3 h-3" /> Split from {lr.split_from.lr_number}
                </button>
              )}
              {(lr.split_into || []).map((s) => (
                <button key={s.id} className="inline-flex items-center gap-1 text-[#68665E] hover:text-[#C84B31] hover:underline" onClick={() => navigate(`/lr/${s.id}`)}>
                  <Split className="w-3 h-3" /> Split into {s.lr_number}
                </button>
              ))}
              {lr.from_quotation_number && (
                <span className="inline-flex items-center gap-1 text-[#68665E]">From Quotation {lr.from_quotation_number}</span>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <RemindMeButton entityType="lr" entityId={lr.id} label={`LR ${lr.lr_number}`} className="btn-secondary" testId="lr-detail-remind" />
          <button className="btn-secondary" onClick={shareTracking} data-testid="lr-detail-share-track"><Share2 className="w-4 h-4" /> Share Tracking</button>
          <button className="btn-secondary text-[#1f8a4c]" onClick={shareWhatsApp} data-testid="lr-detail-share-whatsapp"><MessageCircle className="w-4 h-4" /> WhatsApp</button>
          <button className="btn-secondary" onClick={openSplit} data-testid="lr-detail-split-button"><Scissors className="w-4 h-4" /> Split LR</button>
          <button className="btn-secondary" onClick={() => navigate(`/lrs?edit=${lr.id}`)} data-testid="lr-detail-edit"><Pencil className="w-4 h-4" /> Edit LR</button>
          <button className="btn-primary" onClick={() => generateLRPdf(lr, company)} data-testid="lr-detail-print"><Printer className="w-4 h-4" /> Print PDF</button>
        </div>
      </div>

      <Lifecycle lr={lr} onChanged={refresh} />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Stat label="Route" value={`${lr.from_location} → ${lr.to_location}`} testId="lr-detail-route" />
        <Stat label="Transit Time" value={lr.transit_days == null ? "In transit" : `${lr.transit_days} day(s)`} testId="lr-detail-transit" />
        <div className="bg-white border border-[#E5E3DB] p-4">
          <div className="text-[11px] uppercase tracking-wide text-[#68665E]">Invoice Amount</div>
          {data.invoice ? (
            <>
              <div className="text-lg font-semibold text-[#1A1A18] mt-1" data-testid="lr-detail-invoice-amount">{formatINR(data.invoice.total || 0)}</div>
              <div className="text-[11px] text-[#68665E] mt-1">{data.invoice.invoice_number || "Invoice"}{data.invoice.status ? ` · ${data.invoice.status}` : ""}</div>
            </>
          ) : (
            <div className="text-base font-semibold text-[#A09E96] mt-1" data-testid="lr-detail-invoice-amount">Not invoiced yet</div>
          )}
        </div>
        <div className="bg-white border border-[#E5E3DB] p-4">
          <div className="text-[11px] uppercase tracking-wide text-[#68665E]">Expenses (allocated)</div>
          <div className="text-lg font-semibold text-[#1A1A18] mt-1" data-testid="lr-detail-expense">{formatINR(data.profit?.expense_allocated || 0)}</div>
          {Number(data.profit?.lh_paid || 0) > 0 && (
            <div className="text-[11px] text-[#68665E] mt-1" data-testid="lr-detail-expense-lh-note">incl. {formatINR(data.profit.lh_paid)} paid lorry hire</div>
          )}
        </div>
        <div className="bg-white border border-[#E5E3DB] p-4">
          <div className="text-[11px] uppercase tracking-wide text-[#68665E]">Net Profit</div>
          <div className={`text-lg font-semibold mt-1 ${(data.profit?.profit || 0) >= 0 ? "text-[#2F6F4E]" : "text-[#8A2526]"}`} data-testid="lr-detail-profit">
            {formatINR(data.profit?.profit || 0)} <span className="text-xs font-normal text-[#68665E]">({data.profit?.margin_pct || 0}%)</span>
          </div>
          <div className="text-[11px] text-[#68665E] mt-1" data-testid="lr-detail-revenue-note">
            Revenue {formatINR(data.profit?.revenue || 0)} · {data.profit?.revenue_source === "invoice" ? "from linked invoice" : "quoted freight"}
          </div>
        </div>
      </div>

      {(lr.pod_url || lr.pod_drive_link) && (
        <div className="bg-white border border-[#E5E3DB] p-4 mb-6" data-testid="lr-detail-pod">
          <div className="text-[11px] uppercase tracking-wide text-[#68665E] mb-2">Proof of Delivery</div>
          <div className="flex items-center gap-4">
            {lr.pod_url && <PodThumb url={lr.pod_url} testId="lr-detail-pod" />}
            {lr.pod_drive_link && (
              <a href={lr.pod_drive_link} target="_blank" rel="noreferrer" className="btn-ghost text-sm" data-testid="lr-detail-pod-drive">Open Drive copy</a>
            )}
          </div>
        </div>
      )}

      <Parties lr={lr} pickups={pickups} deliveries={deliveries} navigate={navigate} />
      <Transport lr={lr} pickups={pickups} deliveries={deliveries} carrier={carrier} hasCarrier={hasCarrier} hirer={hirer} hasHirer={hasHirer} />
      <Cargo lr={lr} lrItems={lrItems} vehicles={vehicles} />
      <LiveTracking lr={lr} />
      <History lr={lr} data={data} navigate={navigate} docTypes={docTypes} />

      {showSplit && (
        <LRSplitModal
          lr={lr} lrItems={lrItems} vehicles={vehicles}
          splitItems={splitItems} setSplitItems={setSplitItems}
          splitVehicles={splitVehicles} setSplitVehicles={setSplitVehicles}
          splitNumber={splitNumber} setSplitNumber={setSplitNumber}
          splitFreight={splitFreight} setSplitFreight={setSplitFreight}
          splitting={splitting} doSplit={doSplit} toggle={toggle}
          onClose={() => setShowSplit(false)}
        />
      )}
    </div>
  );
}
